// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// convexRead — one-shot authenticated Convex reads for convexOnly routing
// (#191 gap #1). convexOnly users must NEVER hit the Lambda for app data; these
// helpers let a queryFn/hook fetch the same data from Convex instead.
//
// The frontend convexClient (ConvexReactClient) carries the user's MSAL identity
// (set by ConvexProviderWithAuth), so .query() here is authenticated — gated
// functions (candidates, etc.) work. Results are wrapped in the Lambda envelope
// { message: { result } } so existing select()/parsing keeps working unchanged.
// `decode`: "list" (default) | "doc" | "raw".
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
import { convexClient } from "./convexClient";
import { CONVEX_SOURCE } from "../utility/backendConfig";
import { decodeConvexList, decodeConvexDoc } from "./decodeConvexDoc";

export const CX_SOURCE = CONVEX_SOURCE;

export async function convexReadResult(ref, args, decode = "list") {
  const raw = await convexClient.query(ref, args);
  if (decode === "doc") return raw ? decodeConvexDoc(raw) : raw;
  if (decode === "raw") return raw;
  return decodeConvexList(raw || []);
}

// Lambda-shaped envelope so queryFns whose select() reads
// `response.message.result` work without change.
export async function convexReadEnvelope(ref, args, decode = "list") {
  const result = await convexReadResult(ref, args, decode);
  return { message: { result }, _convexOnly: true };
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Time Machine day-bundle (#114/#191 Stage 3). parseStateAndEventsResponse
// REQUIRES message.method === "getStateAndEventsForDate", so this can't use the
// generic convexReadEnvelope (which omits method). The Convex function returns
// the result with key-ENCODED docs (FileMaker `»` portal fields etc.), so the
// baselineState + events arrays must be decoded before the app replays them.
// Floor-refusal ({error,...}) and runtime errors pass through unchanged so the
// parser routes them to kind:"floor"/"error" exactly like the Lambda.
//
// Note: the Convex TM functions ALWAYS read PROD (source hardcoded, #121), so
// args carries NO source — just { targetCollection, location, date, companyCode }.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
export async function convexReadStateAndEvents(ref, args) {
  // #224: getStateAndEventsForDate is now a Convex ACTION (it fans out to
  // internalQuery sub-calls, each its own 16 MB read budget, so busy offices no
  // longer blow the per-execution cap). Actions are invoked via .action(), not
  // .query() — the function reference name is the same either way.
  const raw = await convexClient.action(ref, args);
  let result = raw;
  if (raw && !raw.error) {
    result = {
      ...raw,
      baselineState: decodeConvexList(raw.baselineState || []),
      events: decodeConvexList(raw.events || []),
    };
  }
  return {
    message: { method: "getStateAndEventsForDate", result },
    _debugVersion: "convex",
    _convexOnly: true,
  };
}

// Time Machine EventLog history (#114 Phase 5/6 — getEventLogByDocumentId /
// getEventLogForCrew / getEventLogForAttributeCell). parseEventLogResponse
// requires message.method === <method> and reads message.result.events; the
// stored events carry key-ENCODED nested before/after images, so decode them.
export async function convexReadEventLog(ref, args, method) {
  // #224: getEventLogForCrew + getEventLogForAttributeCell are now ACTIONS (they
  // gather the unbounded Attributes event window via adaptive time-bisection so
  // a busy deployment can't blow the 16 MB read cap). getEventLogByDocumentId
  // stays a query (bounded by one document's own history). Same ref name works
  // for either; just pick the right client method.
  const isAction =
    method === "getEventLogForCrew" || method === "getEventLogForAttributeCell";
  const raw = isAction
    ? await convexClient.action(ref, args)
    : await convexClient.query(ref, args);
  let result = raw;
  if (raw && !raw.error && Array.isArray(raw.events)) {
    result = { ...raw, events: decodeConvexList(raw.events) };
  }
  return {
    message: { method, result },
    _debugVersion: "convex",
    _convexOnly: true,
  };
}
